import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../utils/cn';
import { ChevronDown } from 'lucide-react';
const SelectContext = React.createContext(null);
const Select = ({ value, onValueChange, children, className, error, label }) => {
    const [open, setOpen] = useState(false);
    return (_jsx(SelectContext.Provider, { value: { value, onValueChange, open, setOpen }, children: _jsxs("div", { className: cn('relative', className), children: [label && (_jsx("label", { className: "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1.5", children: label })), children, error && (_jsx("p", { className: "mt-1.5 text-xs font-medium text-red-600", children: error }))] }) }));
};
const SelectTrigger = ({ children, className, ...props }) => {
    const context = React.useContext(SelectContext);
    return (_jsxs("button", { type: "button", className: cn('flex h-10 w-full items-center justify-between rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 hover:border-gray-300 dark:hover:border-gray-600', className), onClick: () => context?.setOpen(!context.open), ...props, children: [children, _jsx(ChevronDown, { className: cn('w-4 h-4 text-gray-400 transition-transform duration-200', context?.open && 'rotate-180') })] }));
};
const SelectValue = ({ placeholder, className, ...props }) => {
    const context = React.useContext(SelectContext);
    return (_jsx("span", { className: cn('truncate text-sm', !context?.value && 'text-gray-400', className), ...props, children: context?.value || placeholder }));
};
const SelectContent = ({ children, className, ...props }) => {
    const context = React.useContext(SelectContext);
    const ref = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                context?.setOpen(false);
            }
        };
        if (context?.open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [context?.open, context?.setOpen]);
    if (!context?.open)
        return null;
    return (_jsx("div", { ref: ref, className: cn('absolute z-50 mt-1.5 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg shadow-black/5 py-1 animate-scale-in overflow-hidden', className), ...props, children: children }));
};
const SelectItem = ({ value, children, className, ...props }) => {
    const context = React.useContext(SelectContext);
    const isSelected = context?.value === value;
    const handleClick = () => {
        if (value)
            context?.onValueChange?.(value);
        context?.setOpen(false);
    };
    return (_jsx("div", { className: cn('relative flex w-full cursor-pointer select-none items-center rounded-lg px-3 py-2 text-sm transition-colors duration-100 hover:bg-gray-100 dark:hover:bg-gray-700', isSelected && 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-400 font-medium', className), onClick: handleClick, ...props, children: children }));
};
export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
